/**
 * System Info Collector
 *
 * Collects browser, OS, and environment information for bug reports.
 */

import { getRecentErrors, type CapturedError } from './error-capture';

export interface SystemInfo {
  // Browser info
  browser: string;
  browserVersion: string;
  // OS info
  os: string;
  osVersion: string;
  // Device info
  platform: string;
  isMobile: boolean;
  // Screen info
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  // Page info
  currentUrl: string;
  referrer: string;
  // App info
  appVersion: string;
  // Network
  online: boolean;
  connectionType?: string;
  // Language
  language: string;
  languages: string[];
  // Timezone
  timezone: string;
  timezoneOffset: number;
  // Memory (if available)
  jsHeapSizeLimit?: number;
  usedJSHeapSize?: number;
  // Recent errors
  recentErrors: CapturedError[];
}

/**
 * Parse user agent to extract browser info
 */
function parseBrowser(ua: string): { name: string; version: string } {
  // Order matters - check more specific ones first
  const browsers = [
    { name: 'Edge', pattern: /Edg(?:e|A|iOS)?\/(\d+[\d.]*)/ },
    { name: 'Opera', pattern: /(?:Opera|OPR)\/(\d+[\d.]*)/ },
    { name: 'Brave', pattern: /Brave\/(\d+[\d.]*)/ },
    { name: 'Chrome', pattern: /Chrome\/(\d+[\d.]*)/ },
    { name: 'Firefox', pattern: /Firefox\/(\d+[\d.]*)/ },
    { name: 'Safari', pattern: /Version\/(\d+[\d.]*).*Safari/ },
    { name: 'IE', pattern: /(?:MSIE |rv:)(\d+[\d.]*)/ },
  ];

  for (const browser of browsers) {
    const match = ua.match(browser.pattern);
    if (match) {
      return { name: browser.name, version: match[1] };
    }
  }

  return { name: 'Unknown', version: 'Unknown' };
}

/**
 * Parse user agent to extract OS info
 */
function parseOS(ua: string): { name: string; version: string } {
  const osPatterns = [
    { name: 'Windows 11', pattern: /Windows NT 10\.0.*Win64/ },
    { name: 'Windows 10', pattern: /Windows NT 10\.0/ },
    { name: 'Windows 8.1', pattern: /Windows NT 6\.3/ },
    { name: 'Windows 8', pattern: /Windows NT 6\.2/ },
    { name: 'Windows 7', pattern: /Windows NT 6\.1/ },
    { name: 'macOS', pattern: /Mac OS X (\d+[._]\d+(?:[._]\d+)?)/ },
    { name: 'iOS', pattern: /(?:iPhone|iPad|iPod).*OS (\d+[._]\d+)/ },
    { name: 'Android', pattern: /Android (\d+[\d.]*)/ },
    { name: 'Linux', pattern: /Linux/ },
    { name: 'Chrome OS', pattern: /CrOS/ },
  ];

  for (const os of osPatterns) {
    const match = ua.match(os.pattern);
    if (match) {
      let version = 'Unknown';
      if (match[1]) {
        version = match[1].replace(/_/g, '.');
      }
      return { name: os.name, version };
    }
  }

  return { name: 'Unknown', version: 'Unknown' };
}

/**
 * Check if device is mobile
 */
function isMobileDevice(): boolean {
  const ua = navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

/**
 * Get connection type if available
 */
function getConnectionType(): string | undefined {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      type?: string;
    };
  };

  if (nav.connection) {
    return nav.connection.effectiveType || nav.connection.type;
  }
  return undefined;
}

/**
 * Get memory info if available
 */
function getMemoryInfo(): { jsHeapSizeLimit?: number; usedJSHeapSize?: number } {
  const perf = performance as Performance & {
    memory?: {
      jsHeapSizeLimit: number;
      usedJSHeapSize: number;
    };
  };

  if (perf.memory) {
    return {
      jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
      usedJSHeapSize: perf.memory.usedJSHeapSize,
    };
  }
  return {};
}

/**
 * Get app version from version.json
 */
async function getAppVersion(): Promise<string> {
  try {
    const response = await fetch('/version.json');
    if (response.ok) {
      const data = await response.json();
      return data.version || 'Unknown';
    }
  } catch {
    // Fallback - version might be injected at build time
  }
  return 'Unknown';
}

/**
 * Collect all system information
 */
export async function collectSystemInfo(): Promise<SystemInfo> {
  const ua = navigator.userAgent;
  const browser = parseBrowser(ua);
  const os = parseOS(ua);
  const memory = getMemoryInfo();
  const appVersion = await getAppVersion();

  return {
    // Browser
    browser: browser.name,
    browserVersion: browser.version,
    // OS
    os: os.name,
    osVersion: os.version,
    // Device
    platform: navigator.platform,
    isMobile: isMobileDevice(),
    // Screen
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    // Page
    currentUrl: window.location.href,
    referrer: document.referrer,
    // App
    appVersion,
    // Network
    online: navigator.onLine,
    connectionType: getConnectionType(),
    // Language
    language: navigator.language,
    languages: [...navigator.languages],
    // Timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    // Memory
    ...memory,
    // Recent errors
    recentErrors: getRecentErrors(),
  };
}

/**
 * Get a simplified system info object for display
 */
export function getSystemInfoSummary(info: SystemInfo): string {
  const parts = [
    `${info.browser} ${info.browserVersion}`,
    `${info.os} ${info.osVersion}`,
    `${info.viewportWidth}x${info.viewportHeight}`,
    info.isMobile ? 'Mobile' : 'Desktop',
  ];

  if (info.recentErrors.length > 0) {
    parts.push(`${info.recentErrors.length} recent error(s)`);
  }

  return parts.join(' | ');
}
